import React, { FormEvent, useState } from 'react';
import { Head, Link, router } from '@inertiajs/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

interface SignupProps {
  errors?: Record<string, string>;
  formData?: {
    name?: string;
    email?: string;
  };
  isGoogleSignup?: boolean;
}

const Signup: React.FC<SignupProps> = ({
  errors = {},
  formData = {},
}) => {
  const [name, setName] = useState(formData.name || '');
  const [email, setEmail] = useState(formData.email || '');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [processing, setProcessing] = useState(false);
  
  // Determine if this is a Google OAuth signup (has name/email from OAuth)
  const isGoogleSignup = !!(formData.name && formData.email);
  
  // Email validation regex pattern
  const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

  const validateEmail = (emailToValidate: string): boolean => {
    return emailRegex.test(emailToValidate);
  };

  const validatePassword = (pass: string, passConf: string): boolean => {
    if (pass.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return false;
    }
    if (pass !== passConf) {
      setPasswordError('Passwords do not match');
      return false;
    }
    setPasswordError('');
    return true;
  };

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    
    // Client-side email validation
    if (!validateEmail(email)) {
      return;
    }
    
    // Skip password validation for Google OAuth signup
    if (!isGoogleSignup) {
      // Client-side password validation
      if (!validatePassword(password, passwordConfirmation)) {
        return;
      }
    }
    
    setProcessing(true);
    
    // Use router.post for full control over the data
    router.post('/signup', {
      user: {
        name,
        email,
        password,
        password_confirmation: passwordConfirmation,
        agreed: agreed ? '1' : '0'
      }
    });
  }

  const [showGoogleModal, setShowGoogleModal] = useState(false);
  const [googleAgreed, setGoogleAgreed] = useState(false);

  function handleGoogleClick(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    
    // If already agreed, set cookie and redirect immediately
    if (agreed) {
      setConsentCookie();
      window.location.href = '/users/auth/google_oauth2';
      return;
    }
    
    // Show modal to get consent first
    setGoogleAgreed(false);
    setShowGoogleModal(true);
  }

  function setConsentCookie() {
    const consentData = {
      agreed: true,
      timestamp: new Date().toISOString()
    };
    document.cookie = `consent=${encodeURIComponent(JSON.stringify(consentData))}; path=/; max-age=300`;
    // Also set origin cookie to track that we came from signup page
    document.cookie = `oauth_origin=signup; path=/; max-age=300`;
  }

  function handleGoogleConsentAndContinue() {
    // Also check the main form checkbox to sync state
    setAgreed(true);
    setConsentCookie();
    setShowGoogleModal(false);
    window.location.href = '/users/auth/google_oauth2';
  }

  return (
    <div className="min-h-screen w-full bg-background flex items-center justify-center p-4 md:p-6 lg:p-8">
      <Head title="Create Your Account">
        <meta property="og:title" content="Create Your Account" />
        <meta property="og:description" content="Join us today to get started" />
        <meta property="og:image" content="https://hd.supavp.com/og-image.svg" />
        <meta property="og:url" content="https://hd.supavp.com/signup" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Create Your Account" />
        <meta name="twitter:description" content="Join us today to get started" />
        <meta name="twitter:image" content="https://hd.supavp.com/og-image.svg" />
      </Head>

      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
            {isGoogleSignup ? 'Complete Your Signup' : 'Create Your Account'}
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base">
            {isGoogleSignup 
              ? `Welcome ${formData.name || ''}! Please agree to our terms to complete your account.`
              : 'Join us today to get started'}
          </p>
        </div>

        {/* Form Card */}
        <Card className="p-5 sm:p-6 md:p-8">
          {/* Google Sign-In Button */}
          <div className="mb-6">
            <a
              href="/users/auth/google_oauth2"
              onClick={handleGoogleClick}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors duration-200 cursor-pointer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="20"
                height="20"
                className="shrink-0"
              >
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </a>
          </div>

          {/* Or Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-card text-muted-foreground">Or</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Global Errors */}
            {Object.keys(errors).length > 0 && (
              <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-md">
                <p className="font-medium">Please correct the following errors:</p>
                <ul className="list-disc list-inside mt-2">
                  {Object.entries(errors).map(([key, value]) => (
                    <li key={key}>
                      {typeof value === 'string' ? value : Array.isArray(value) ? value[0] : value}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Name Field */}
            <div className="flex flex-col space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your full name"
                required
                className={errors.name ? 'border-destructive' : ''}
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name}</p>
              )}
            </div>

            {/* Email Field */}
            <div className="flex flex-col space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                required
                className={errors.email ? 'border-destructive' : ''}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email}</p>
              )}
            </div>

            {/* Password Field - hidden for Google OAuth signup */}
            {!isGoogleSignup && (
              <div className="flex flex-col space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a password"
                  required
                  className={errors.password ? 'border-destructive' : ''}
                />
                {errors.password && (
                  <p className="text-sm text-destructive">{errors.password}</p>
                )}
              </div>
            )}

            {/* Confirm Password Field - hidden for Google OAuth signup */}
            {!isGoogleSignup && (
              <div className="flex flex-col space-y-2">
                <Label htmlFor="password_confirmation">Confirm Password</Label>
                <Input
                  id="password_confirmation"
                  type="password"
                  value={passwordConfirmation}
                  onChange={(e) => setPasswordConfirmation(e.target.value)}
                  placeholder="Confirm your password"
                  required
                  className={(passwordError || errors.password_confirmation) ? 'border-destructive' : ''}
                />
                {(passwordError || errors.password_confirmation) && (
                  <p className="text-sm text-destructive">{passwordError || errors.password_confirmation}</p>
                )}
              </div>
            )}

            {/* Consent Checkbox */}
            <div className="flex items-start space-x-3">
              <Checkbox
                id="consent"
                checked={agreed}
                onCheckedChange={(checked) => setAgreed(checked === true)}
                className="mt-1"
              />
              <div className="flex flex-col">
                <label
                  htmlFor="consent"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  I agree to the{' '}
                  <Link href="/terms" className="text-primary hover:underline" target="_blank">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link href="/privacy" className="text-primary hover:underline" target="_blank">
                    Privacy Policy
                  </Link>
                </label>
                <p className="text-xs text-muted-foreground mt-1">
                  I have read and agree to the terms governing my use of the service
                </p>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-2">
              <Button
                type="submit"
                className="w-full"
                disabled={processing || !agreed}
              >
                {processing ? 'Creating Account...' : 'Create Account'}
              </Button>
            </div>
          </form>
        </Card>

        {/* Footer - Sign in link */}
        <div className="text-center mt-6 text-xs sm:text-sm text-muted-foreground">
          <p>
            Already have an account?{' '}
            <Link
              href="/signin"
              className="text-primary hover:underline font-medium"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Google OAuth Consent Modal */}
      <Dialog open={showGoogleModal} onOpenChange={setShowGoogleModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Continue with Google</DialogTitle>
            <DialogDescription>
              Before signing up with Google, you need to agree to our terms.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex items-start space-y-4 py-4">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="google-consent"
                checked={googleAgreed}
                onCheckedChange={(checked) => setGoogleAgreed(checked === true)}
                className="mt-1"
              />
              <div className="flex flex-col">
                <label
                  htmlFor="google-consent"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  I agree to the{' '}
                  <Link href="/terms" className="text-primary hover:underline" target="_blank">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link href="/privacy" className="text-primary hover:underline" target="_blank">
                    Privacy Policy
                  </Link>
                </label>
                <p className="text-xs text-muted-foreground mt-1">
                  I have read and agree to the terms governing my use of the service
                </p>
              </div>
            </div>
          </div>

          <DialogFooter className="sm:justify-between gap-2">
            <Button
              variant="outline"
              onClick={() => setShowGoogleModal(false)}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              onClick={handleGoogleConsentAndContinue}
              disabled={!googleAgreed}
              className="w-full sm:w-auto"
            >
              Agree & Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Signup;
